import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { House } from '../../data/houses';

interface ScreensaverProps {
  houses: House[];
  onDismiss: () => void;
}

interface FloatingIcon {
  id: number;
  house: House;
  x: number;
  y: number;
  scale: number;
  rotation: number;
  duration: number;
}

export default function Screensaver({ houses, onDismiss }: ScreensaverProps) {
  const [icons, setIcons] = useState<FloatingIcon[]>([]);

  useEffect(() => {
    const generateIcons = () => {
      const newIcons: FloatingIcon[] = [];
      const count = Math.min(houses.length * 3, 15);

      for (let i = 0; i < count; i++) {
        const house = houses[i % houses.length];
        newIcons.push({
          id: i,
          house,
          x: Math.random() * 80 + 10,
          y: Math.random() * 80 + 10,
          scale: Math.random() * 0.5 + 0.8,
          rotation: Math.random() * 360,
          duration: Math.random() * 10 + 15,
        });
      }
      setIcons(newIcons);
    };

    generateIcons();
  }, [houses]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onDismiss}
        className="fixed inset-0 z-50 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 cursor-pointer overflow-hidden"
      >
        {/* Floating House Icons */}
        {icons.map(icon => (
          <motion.div
            key={icon.id}
            className="absolute"
            initial={{
              x: `${icon.x}vw`,
              y: `${icon.y}vh`,
              scale: icon.scale,
              rotate: icon.rotation,
            }}
            animate={{
              x: [`${icon.x}vw`, `${(icon.x + 20) % 90}vw`, `${icon.x}vw`],
              y: [`${icon.y}vh`, `${(icon.y + 15) % 90}vh`, `${icon.y}vh`],
              rotate: [icon.rotation, icon.rotation + 360],
            }}
            transition={{
              duration: icon.duration,
              repeat: Infinity,
              ease: 'linear',
            }}
          >
            <div
              className="text-6xl md:text-8xl opacity-30 hover:opacity-60 transition-opacity"
              style={{
                filter: `drop-shadow(0 0 20px ${icon.house.color})`,
              }}
            >
              {icon.house.icon}
            </div>
          </motion.div>
        ))}

        {/* Center Content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-center space-y-8"
          >
            <h1 className="text-5xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 tracking-wider">
              HOUSE POINTS
            </h1>

            <div className="flex items-center justify-center space-x-4">
              {houses.slice(0, 5).map((house, index) => (
                <motion.span
                  key={house.id}
                  className="text-4xl md:text-6xl"
                  animate={{
                    y: [0, -10, 0],
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: index * 0.2,
                  }}
                >
                  {house.icon}
                </motion.span>
              ))}
            </div>

            <motion.p
              className="text-white/60 text-xl md:text-2xl"
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Tap anywhere to begin
            </motion.p>
          </motion.div>
        </div>

        {/* Ambient Glow Effects */}
        <div className="absolute inset-0 pointer-events-none">
          {houses.slice(0, 4).map((house, index) => (
            <motion.div
              key={house.id}
              className="absolute w-96 h-96 rounded-full blur-3xl opacity-20"
              style={{ backgroundColor: house.color }}
              initial={{
                x: index % 2 === 0 ? '-20%' : '80%',
                y: index < 2 ? '-20%' : '80%',
              }}
              animate={{
                x: index % 2 === 0 ? ['0%', '20%', '0%'] : ['80%', '60%', '80%'],
                y: index < 2 ? ['0%', '20%', '0%'] : ['80%', '60%', '80%'],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
